import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {faEllipsisVertical, faReply, faXmark} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Dropdown, Flex} from 'antd';
import {useEffect} from 'react';

import {Text} from '../../../../../component/text/text';
import {Locale} from '../../../../../provider/locale/locale';
import {LangKeyType} from '../../../../../provider/locale/translation/type';
import {useSystem} from '../../../../../provider/system/system-hook';

import * as styles from './reviews-bulk-selection-bar.scss';

type MenuItemConfigType = {
    langKey: LangKeyType;
    onClick: VoidFunction;
    icon?: IconDefinition;
};

type PropsType = {
    selectedCount: number;
    isVisible: boolean;

    toggleAllButtonConfig: {
        langKey: LangKeyType;
        onClick: VoidFunction;
    };

    onCancel: VoidFunction;

    submitButtonConfig: MenuItemConfigType;
    secondaryButtonConfig?: MenuItemConfigType;
    thirdButtonConfig?: MenuItemConfigType;
    fourthButtonConfig?: MenuItemConfigType;
};

export function ReviewsBulkSelectionBar(props: PropsType): JSX.Element | null {
    const {
        selectedCount,
        isVisible,
        toggleAllButtonConfig,
        onCancel,
        submitButtonConfig,
        secondaryButtonConfig,
        thirdButtonConfig,
        fourthButtonConfig,
    } = props;

    const {screen} = useSystem();

    useEffect(() => {
        if ((screen.isDesktop || screen.isTablet || screen.isMobile) && isVisible) {
            document.body.dataset.tableControlPanelOpen = 'true';
        } else {
            Reflect.deleteProperty(document.body.dataset, 'tableControlPanelOpen');
        }

        return () => {
            Reflect.deleteProperty(document.body.dataset, 'tableControlPanelOpen');
        };
    }, [isVisible, screen.isDesktop, screen.isTablet, screen.isMobile]);

    if (!isVisible) {
        return null;
    }

    const isDisabled = selectedCount === 0;

    const menuItems = [
        ...(secondaryButtonConfig
            ? [
                  {
                      key: 'secondary',
                      icon: secondaryButtonConfig.icon ? <FontAwesomeIcon icon={secondaryButtonConfig.icon} /> : null,
                      label: <Locale stringKey={secondaryButtonConfig.langKey} />,
                      onClick: secondaryButtonConfig.onClick,
                      disabled: isDisabled,
                  },
              ]
            : []),
        ...(thirdButtonConfig
            ? [
                  {
                      key: 'third',
                      icon: thirdButtonConfig.icon ? <FontAwesomeIcon icon={thirdButtonConfig.icon} /> : null,
                      label: <Locale stringKey={thirdButtonConfig.langKey} />,
                      onClick: thirdButtonConfig.onClick,
                      disabled: isDisabled,
                  },
              ]
            : []),
        ...(fourthButtonConfig
            ? [
                  {
                      key: 'fourth',
                      icon: fourthButtonConfig.icon ? <FontAwesomeIcon icon={fourthButtonConfig.icon} /> : null,
                      label: <Locale stringKey={fourthButtonConfig.langKey} />,
                      onClick: fourthButtonConfig.onClick,
                      disabled: isDisabled,
                  },
              ]
            : []),
    ];

    return (
        <div className={styles.ReviewsBulkSelectionBar}>
            <Flex align="center" gap={12}>
                <span className={styles.ReviewsBulkSelectionBar_countBadge}>
                    <Text bolder inheritColor>
                        {selectedCount}
                    </Text>
                </span>

                <Text className={styles.ReviewsBulkSelectionBar_selectedLabel} stringKey="REVIEWS__SELECTED_LABEL" />
                <Button
                    className={styles.ReviewsBulkSelectionBar_toggleAll}
                    onClick={() => toggleAllButtonConfig.onClick()}
                    size="small"
                    type="link"
                >
                    <Locale stringKey={toggleAllButtonConfig.langKey} />
                </Button>
            </Flex>
            <Flex align="center" gap={8}>
                <Button
                    className={styles.ReviewsBulkSelectionBar_submitButton}
                    disabled={isDisabled}
                    onClick={() => {
                        submitButtonConfig.onClick();
                    }}
                    type="primary"
                >
                    <span className={styles.ReviewsBulkSelectionBar_submitLabel}>
                        <Locale stringKey={submitButtonConfig.langKey} />
                    </span>
                    <span className={styles.ReviewsBulkSelectionBar_submitIcon}>
                        <FontAwesomeIcon icon={faReply} />
                    </span>
                </Button>
                {menuItems.length > 0 && (
                    <Dropdown
                        getPopupContainer={(triggerNode) => triggerNode.parentElement ?? triggerNode}
                        menu={{items: menuItems}}
                        placement="topRight"
                        trigger={['click']}
                    >
                        <Button
                            className={styles.ReviewsBulkSelectionBar_menuButton}
                            icon={<FontAwesomeIcon icon={faEllipsisVertical} />}
                            type="text"
                        />
                    </Dropdown>
                )}
                <Button
                    className={styles.ReviewsBulkSelectionBar_cancelButton}
                    icon={<FontAwesomeIcon icon={faXmark} />}
                    onClick={onCancel}
                    type="text"
                />
            </Flex>
        </div>
    );
}
